import React from 'react';
import { useTranslation } from 'react-i18next';
import _get from 'lodash/get';
import { Site } from 'graphql-common';
import { Values } from '@lib/interfaces/form';
import DashboardContentMaxWidth from '@lib/layouts/DashboardLayout/DashboardContentMaxWidth/DashboardContentMaxWidth';
import PageHeader from '@lib/layouts/DashboardLayout/PageHeader/PageHeader';
import PageBody from '@lib/layouts/DashboardLayout/PageBody/PageBody';
import Trans from '@lib/components/Trans/Trans';
import Button, { ButtonTypes } from '@lib/components/Button/Button';
import { APP_URLS } from 'constants/urls';
import ConfirmationModal from '@lib/components/ConfirmationModal/ConfirmationModal';
import { materialSymbolsOutlined } from '@lib/enums/common';
import WhiteBox from '@lib/components/WhiteBox/WhiteBox';
import Form from '@lib/components/ReactHookForm/FormContainer';
import replaceNullsWithUndefined from '@lib/utils/replaceNullsWithUndefined';
import { Steps } from '@lib/enums/form';
import UserModalForm from 'components/UserModalForm/UserModalForm';
import ErrorPage from '@lib/components/ErrorPage/ErrorPage';
import getStatusFromError from '@lib/utils/getStatusFromError';
import getDescriptionFromError from '@lib/utils/getDescriptionFromError';
import CircleLoader from '@lib/components/CircleLoader/CircleLoader';
import { FormRefType } from '@lib/components/ReactHookForm/types';
import { IconName } from '@lib/components/Modal/enums';
import { UseSiteActionsResult } from 'routes/Sites/utils/useSiteActions';
import {
  Modals,
  UseModalManagementResult,
} from '@lib/hooks/useModalManagement';
import entityViewStyles from '@lib/assets/styles/EntityView.module.scss';
import { SiteFieldNames } from '@lib/enums/fieldNames/siteFieldNames';
import { getGraphQLErrors } from '@lib/utils/graphQLErrors';
import SiteEditFormActions from './SiteEditFormActions';
import getSiteFormFields from '../utils/getSiteFormFields';
import getSiteDefaultValuesFromQueryData from '../utils/getSiteDefaultValuesFromQueryData';

interface Props {
  formRef: FormRefType;
  useSiteActionsResult: UseSiteActionsResult;
  useModalManagementResult: UseModalManagementResult;
}

function SiteComponent(props: Props) {
  const { formRef, useSiteActionsResult, useModalManagementResult } = props;
  const {
    countriesCodes,
    destroyLoading,
    isSectorRequired,
    onConfirmedDelete,
    onCreateUserCallback,
    onEdit,
    usersLoadOptions,
    sectorsLoadOptions,
    usersQueryHookResult,
    responsiblePersonLoadOptions,
    siteQuery,
    updateMutationResult,
  } = useSiteActionsResult;
  const { openModal, closeModal, isModalOpened } = useModalManagementResult;
  const openUserFormModal = () => openModal(Modals.UserFormModal);
  const closeUserFormModal = () => closeModal(Modals.UserFormModal);
  const openSiteDeleteModal = () => openModal(Modals.SiteDestroyModal);
  const closeSiteDeleteModal = () => closeModal(Modals.SiteDestroyModal);
  const { t } = useTranslation();
  const { allIds: assigneeIds = [] } = usersQueryHookResult;
  const siteData = _get(siteQuery, 'data.site') as Site;

  const fields = getSiteFormFields({
    assigneeIds,
    countriesCodes,
    isEditForm: true,
    isSectorRequired,
    openUserFormModal,
    responsiblePersonLoadOptions,
    sectorsLoadOptions,
    site: siteData,
    t,
    usersLoadOptions,
  });

  const defaultValues = replaceNullsWithUndefined(
    getSiteDefaultValuesFromQueryData({
      data: siteQuery.data?.site as Values,
      assigneeIds,
      t,
    }),
  );

  if (siteQuery.error) {
    return (
      <ErrorPage
        status={getStatusFromError(siteQuery.error)}
        description={getDescriptionFromError(siteQuery.error)}
      />
    );
  }

  return (
    <DashboardContentMaxWidth>
      <PageHeader title={t('edit-site')}>
        <Button
          buttonText={t('view-site')}
          buttonType={ButtonTypes.primaryOutlined}
          leftIcon="visibility"
          to={APP_URLS.dashboard.sites.view.getDynamicPath({
            pathParts: {
              id: `${siteData?.id}`,
            },
          })}
        />
      </PageHeader>
      <PageBody>
        {siteQuery.loading ? (
          <CircleLoader />
        ) : (
          <>
            <WhiteBox verticalPaddings="large">
              <Form
                actionsComponent={SiteEditFormActions}
                defaultValues={defaultValues}
                fields={fields}
                formId={`site-${siteData.id}-${siteData.updatedAt}`}
                formRef={formRef}
                graphQLErrors={getGraphQLErrors(updateMutationResult.error)}
                graphQLErrorsReplacePathMap={{
                  'geojson.features': SiteFieldNames.LocationPoint,
                  sectorId: SiteFieldNames.Sector,
                }}
                loading={updateMutationResult.loading}
                onSubmit={onEdit}
                steps={[
                  { title: t('site-step-1') },
                  { title: t('site-step-2'), optional: true },
                  { title: t('site-step-3'), optional: true },
                ]}
                stepsCount={Steps.step3}
              />
            </WhiteBox>
            <div className={entityViewStyles.actions}>
              <Button
                buttonText={t('delete-site')}
                buttonType={ButtonTypes.negativeOutlined}
                leftIcon="delete"
                leftIconClassName={materialSymbolsOutlined}
                onClick={openSiteDeleteModal}
              />
            </div>
          </>
        )}
      </PageBody>
      <UserModalForm
        isOpen={isModalOpened(Modals.UserFormModal)}
        onClose={closeUserFormModal}
        onSubmitUserFormCallback={onCreateUserCallback}
        title={t('add-team-member')}
      />
      <ConfirmationModal
        confirmButtonLabel={t('yes-delete')}
        description={<Trans i18nKey="delete-site-text" />}
        icon={IconName.Delete}
        isOpen={isModalOpened(Modals.SiteDestroyModal)}
        loading={destroyLoading}
        onClose={closeSiteDeleteModal}
        onConfirm={onConfirmedDelete}
        title={t('are-you-sure')}
        useProgress
      />
    </DashboardContentMaxWidth>
  );
}

export default SiteComponent;
